import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator, Delete, RotateCcw, Copy, History } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface CalculationHistory {
  expression: string;
  result: string;
  timestamp: Date;
}

export const CalculatorTool: React.FC = () => {
  const { t } = useTranslation();
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [history, setHistory] = useState<CalculationHistory[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [memory, setMemory] = useState(0);

  const inputNumber = (num: string) => {
    if (waitingForOperand) {
      setDisplay(num);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? num : display + num);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (display.indexOf('.') === -1) {
      setDisplay(display + '.');
    }
  };

  const clear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const clearEntry = () => {
    setDisplay('0');
  };

  const backspace = () => {
    if (display.length > 1) {
      setDisplay(display.slice(0, -1));
    } else {
      setDisplay('0');
    }
  };

  const performOperation = (nextOperation: string) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const currentValue = previousValue || 0;
      const newValue = calculate(currentValue, inputValue, operation);

      setDisplay(String(newValue));
      setPreviousValue(newValue);
    }

    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  const calculate = (firstValue: number, secondValue: number, operation: string): number => {
    switch (operation) {
      case '+':
        return firstValue + secondValue;
      case '-':
        return firstValue - secondValue;
      case '×':
        return firstValue * secondValue;
      case '÷':
        return secondValue !== 0 ? firstValue / secondValue : 0;
      case '%':
        return firstValue % secondValue;
      case '^':
        return Math.pow(firstValue, secondValue);
      default:
        return secondValue;
    }
  };

  const performEquals = () => {
    const inputValue = parseFloat(display);

    if (previousValue !== null && operation) {
      const newValue = calculate(previousValue, inputValue, operation);
      const expression = `${previousValue} ${operation} ${inputValue}`;
      
      // Add to history
      const historyItem: CalculationHistory = {
        expression,
        result: String(newValue),
        timestamp: new Date()
      };
      
      setHistory(prev => [historyItem, ...prev.slice(0, 19)]); // Keep last 20 calculations
      
      setDisplay(String(newValue));
      setPreviousValue(null);
      setOperation(null);
      setWaitingForOperand(true);
    }
  };

  const performFunction = (func: string) => {
    const inputValue = parseFloat(display);
    let result: number;
    let expression: string;

    switch (func) {
      case 'sqrt':
        result = Math.sqrt(inputValue);
        expression = `√${inputValue}`;
        break;
      case 'square':
        result = inputValue * inputValue;
        expression = `${inputValue}²`;
        break;
      case 'reciprocal':
        result = inputValue !== 0 ? 1 / inputValue : 0;
        expression = `1/${inputValue}`;
        break;
      case 'sin':
        result = Math.sin(inputValue * Math.PI / 180);
        expression = `sin(${inputValue}°)`;
        break;
      case 'cos':
        result = Math.cos(inputValue * Math.PI / 180);
        expression = `cos(${inputValue}°)`;
        break;
      case 'tan':
        result = Math.tan(inputValue * Math.PI / 180);
        expression = `tan(${inputValue}°)`;
        break;
      case 'log':
        result = inputValue > 0 ? Math.log10(inputValue) : 0;
        expression = `log(${inputValue})`;
        break;
      case 'ln':
        result = inputValue > 0 ? Math.log(inputValue) : 0;
        expression = `ln(${inputValue})`;
        break;
      case 'factorial':
        result = factorial(Math.floor(Math.abs(inputValue)));
        expression = `${Math.floor(Math.abs(inputValue))}!`;
        break;
      default:
        return;
    }

    // Add to history
    const historyItem: CalculationHistory = {
      expression,
      result: String(result),
      timestamp: new Date()
    };
    
    setHistory(prev => [historyItem, ...prev.slice(0, 19)]);
    setDisplay(String(result));
    setWaitingForOperand(true);
  };

  const factorial = (n: number): number => {
    if (n <= 1) return 1;
    return n * factorial(n - 1);
  };

  const memoryStore = () => {
    setMemory(parseFloat(display));
    toast.success(t('tools.calculator.memoryStored'));
  };

  const memoryRecall = () => {
    setDisplay(String(memory));
    setWaitingForOperand(true);
  };

  const memoryClear = () => {
    setMemory(0);
    toast.success(t('tools.calculator.memoryCleared'));
  };

  const memoryAdd = () => {
    setMemory(memory + parseFloat(display));
    toast.success(t('tools.calculator.memoryAdded'));
  };

  const memorySubtract = () => {
    setMemory(memory - parseFloat(display));
    toast.success(t('tools.calculator.memorySubtracted'));
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const useHistoryItem = (item: CalculationHistory) => {
    setDisplay(item.result);
    setWaitingForOperand(true);
    setShowHistory(false);
  };

  // Keyboard support
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      const { key } = event;
      
      if (key >= '0' && key <= '9') {
        inputNumber(key);
      } else if (key === '.') {
        inputDecimal();
      } else if (key === '+') {
        performOperation('+');
      } else if (key === '-') {
        performOperation('-');
      } else if (key === '*') {
        performOperation('×');
      } else if (key === '/') {
        event.preventDefault();
        performOperation('÷');
      } else if (key === 'Enter' || key === '=') {
        performEquals();
      } else if (key === 'Escape') {
        clear();
      } else if (key === 'Backspace') {
        backspace();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [display, previousValue, operation, waitingForOperand]);

  const buttonClass = "h-12 text-lg font-medium transition-all duration-150 hover:scale-105 active:scale-95";
  const numberButtonClass = `${buttonClass} bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600`;
  const operatorButtonClass = `${buttonClass} bg-blue-500 hover:bg-blue-600 text-white`;
  const functionButtonClass = `${buttonClass} bg-purple-500 hover:bg-purple-600 text-white`;
  const memoryButtonClass = `${buttonClass} bg-green-500 hover:bg-green-600 text-white`;

  return (
    <div className="space-y-6">
      {/* Calculator Display */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Calculator className="w-5 h-5" />
              <span>{t('tools.calculator.name')}</span>
            </div>
            <div className="flex items-center space-x-2">
              {memory !== 0 && (
                <span className="text-sm bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 px-2 py-1 rounded">
                  M: {memory}
                </span>
              )}
              <Button
                onClick={() => setShowHistory(!showHistory)}
                variant="outline"
                size="sm"
              >
                <History className="w-4 h-4" />
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border">
              <div className="text-right">
                {operation && previousValue !== null && (
                  <div className="text-sm text-gray-500 mb-1">
                    {previousValue} {operation}
                  </div>
                )}
                <div className="text-3xl font-mono font-bold text-gray-900 dark:text-gray-100 break-all">
                  {display}
                </div>
              </div>
              <div className="flex justify-end mt-2">
                <Button
                  onClick={() => copyToClipboard(display)}
                  variant="outline"
                  size="sm"
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copy
                </Button>
              </div>
            </div>
          </div>

          {/* Calculator Buttons */}
          <div className="grid grid-cols-6 gap-2">
            {/* Memory Functions */}
            <Button onClick={memoryClear} className={memoryButtonClass} size="sm">MC</Button>
            <Button onClick={memoryRecall} className={memoryButtonClass} size="sm">MR</Button>
            <Button onClick={memoryStore} className={memoryButtonClass} size="sm">MS</Button>
            <Button onClick={memoryAdd} className={memoryButtonClass} size="sm">M+</Button>
            <Button onClick={memorySubtract} className={memoryButtonClass} size="sm">M-</Button>
            <Button onClick={clear} className="h-12 bg-red-500 hover:bg-red-600 text-white" size="sm">C</Button>

            {/* Scientific Functions Row 1 */}
            <Button onClick={() => performFunction('sin')} className={functionButtonClass} size="sm">sin</Button>
            <Button onClick={() => performFunction('cos')} className={functionButtonClass} size="sm">cos</Button>
            <Button onClick={() => performFunction('tan')} className={functionButtonClass} size="sm">tan</Button>
            <Button onClick={() => performFunction('log')} className={functionButtonClass} size="sm">log</Button>
            <Button onClick={() => performFunction('ln')} className={functionButtonClass} size="sm">ln</Button>
            <Button onClick={backspace} className="h-12 bg-orange-500 hover:bg-orange-600 text-white" size="sm">
              <Delete className="w-4 h-4" />
            </Button>

            {/* Scientific Functions Row 2 */}
            <Button onClick={() => performFunction('sqrt')} className={functionButtonClass} size="sm">√</Button>
            <Button onClick={() => performFunction('square')} className={functionButtonClass} size="sm">x²</Button>
            <Button onClick={() => performFunction('reciprocal')} className={functionButtonClass} size="sm">1/x</Button>
            <Button onClick={() => performFunction('factorial')} className={functionButtonClass} size="sm">n!</Button>
            <Button onClick={() => performOperation('^')} className={operatorButtonClass} size="sm">x^y</Button>
            <Button onClick={() => performOperation('%')} className={operatorButtonClass} size="sm">%</Button>

            {/* Number Pad */}
            <Button onClick={() => inputNumber('7')} className={numberButtonClass}>7</Button>
            <Button onClick={() => inputNumber('8')} className={numberButtonClass}>8</Button>
            <Button onClick={() => inputNumber('9')} className={numberButtonClass}>9</Button>
            <Button onClick={() => performOperation('÷')} className={operatorButtonClass}>÷</Button>
            <Button onClick={() => inputNumber('(')} className={functionButtonClass} size="sm">(</Button>
            <Button onClick={() => inputNumber(')')} className={functionButtonClass} size="sm">)</Button>

            <Button onClick={() => inputNumber('4')} className={numberButtonClass}>4</Button>
            <Button onClick={() => inputNumber('5')} className={numberButtonClass}>5</Button>
            <Button onClick={() => inputNumber('6')} className={numberButtonClass}>6</Button>
            <Button onClick={() => performOperation('×')} className={operatorButtonClass}>×</Button>
            <Button onClick={() => inputNumber('π')} className={functionButtonClass} size="sm">π</Button>
            <Button onClick={() => inputNumber('e')} className={functionButtonClass} size="sm">e</Button>

            <Button onClick={() => inputNumber('1')} className={numberButtonClass}>1</Button>
            <Button onClick={() => inputNumber('2')} className={numberButtonClass}>2</Button>
            <Button onClick={() => inputNumber('3')} className={numberButtonClass}>3</Button>
            <Button onClick={() => performOperation('-')} className={operatorButtonClass}>-</Button>
            <Button onClick={clearEntry} className="h-12 bg-yellow-500 hover:bg-yellow-600 text-white col-span-2" size="sm">CE</Button>

            <Button onClick={() => inputNumber('0')} className={`${numberButtonClass} col-span-2`}>0</Button>
            <Button onClick={inputDecimal} className={numberButtonClass}>.</Button>
            <Button onClick={() => performOperation('+')} className={operatorButtonClass}>+</Button>
            <Button onClick={performEquals} className="h-12 bg-green-500 hover:bg-green-600 text-white col-span-2">=</Button>
          </div>
        </CardContent>
      </Card>

      {/* History Panel */}
      {showHistory && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('tools.calculator.calculationHistory')}</span>
              <Button
                onClick={() => setHistory([])}
                variant="outline"
                size="sm"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {t('tools.calculator.clearHistory')}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {history.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                    onClick={() => useHistoryItem(item)}
                  >
                    <div>
                      <div className="font-mono text-sm text-gray-600 dark:text-gray-400">
                        {item.expression}
                      </div>
                      <div className="font-mono font-bold text-gray-900 dark:text-gray-100">
                        = {item.result}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {item.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('tools.calculator.noCalculations')}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Keyboard Shortcuts */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.calculator.keyboardShortcuts')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.calculator.basicOperations')}:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• {t('tools.calculator.shortcuts.numbers')}</li>
                <li>• {t('tools.calculator.shortcuts.decimal')}</li>
                <li>• {t('tools.calculator.shortcuts.add')}</li>
                <li>• {t('tools.calculator.shortcuts.subtract')}</li>
                <li>• {t('tools.calculator.shortcuts.multiply')}</li>
                <li>• {t('tools.calculator.shortcuts.divide')}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{t('tools.calculator.controls')}:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• {t('tools.calculator.shortcuts.calculate')}</li>
                <li>• {t('tools.calculator.shortcuts.clear')}</li>
                <li>• {t('tools.calculator.shortcuts.backspace')}</li>
                <li>• {t('tools.calculator.shortcuts.memory')}</li>
                <li>• {t('tools.calculator.shortcuts.scientific')}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
